import React, { useEffect, useState } from 'react';

import { Web } from '@mui/icons-material';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Breadcrumbs, SubHeader, Button, EdgeReportingWindow } from '../../../components';
import getEdgeReportingMenu from '../getEdgeReportingMenu';
import { userHasAccess, generateReport, getReportData, removeReportData } from '../operations';
import getCSVData from './csv';
import { WrapOnsitePayrollReportForm } from './WrapOnsitePayrollReportForm';
import { WrapOnsitePayrollReportTable } from './WrapOnsitePayrollReportTable';
import Excel from '../../../assets/excel.png';
import { newDateFormatted } from '../../../utils';
import ImageColorChanger from '../../../utils/imageColorChanger';

import './styles.scoped.css';

const WrapOnsitePayrollReport = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { menuItems } = getEdgeReportingMenu(t);
  const { user } = useSelector(state => state.admin);
  const [form, setForm] = useState({
    PayrollCalculatedDateFrom: '',
    PayrollCalculatedDateTo: '',
    WrapProjectCoverageType: 'All',
    WrapProjProjectFullName: '',
    WrapSubProjectName: '',
    JobOrderNumber: '',
  });
  const [loadingResults, setLoadingResults] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [data, setData] = useState([]);

  const fileName = `wrap-onsite-payroll-report-${newDateFormatted.toString()}.csv`;

  useEffect(() => {
    if (menuItems) {
      if (!userHasAccess(user, menuItems.find(i => i.to === window.location.pathname)?.id)) {
        navigate('/');
      }
    }
  }, [menuItems, navigate, user]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleViewResults = async () => {
    if (!form.PayrollCalculatedDateFrom || !form.PayrollCalculatedDateTo) {
      alert('Payroll Calculated Date is required.');
      return;
    }

    setLoadingResults(true);

    try {
      const response = await generateReport({
        endpoint: 'wrap-onsite-payroll-report',
        payload: {
          FromPayrollCalcDate: form.PayrollCalculatedDateFrom,
          ToPayrollCalcDate: form.PayrollCalculatedDateTo,
          WrapProjectCoverageType: form.WrapProjectCoverageType,
          WrapProjProjectFullName: form.WrapProjProjectFullName || '',
          WrapSubProjectName: form.WrapSubProjectName || '',
          JobOrderNumber: form.JobOrderNumber || '',
        },
      });

      if (response.location && response.location !== '') {
        const reportData = await getReportData(response.location);
        if (reportData?.length) {
          await removeReportData(response.location);

          const enhancedData = reportData.map(row => ({
            ...row,
            WProjProjectFullName: form.WrapProjectCoverageType,
            WProjProjectNumber: form.JobOrderNumber || '',
            WProjProjectFullName: form.WrapProjProjectFullName || '',
          }));

          setData(enhancedData);
          setShowResults(true);
        } else {
          alert('No data found matching your criteria.');
        }
      } else {
        alert('No data found matching your criteria.');
      }
    } catch (error) {
      console.log('Error generating report:', error);
      alert('An error occurred while generating the report. Please try again.');
    } finally {
      setLoadingResults(false);
    }
  };

  const handleCancel = () => {
    navigate('/edge-reporting');
  };

  return (
    <>
      <SubHeader
        text="EDGE Reporting"
        buttons={[{ icon: <Web sx={{ color: '#fff', fontSize: 30 }} />, onPress: () => navigate('/dashboard') }]}
        popoverTitle="Need help with your Wrap Onsite Payroll Report?"
      />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px',
        }}>
        <Breadcrumbs
          leftTitle="Reporting Home"
          middleTitle={showResults && 'Wrap Onsite Payroll Report'}
          midItemAction={() => setShowResults(false)}
          rightTitle={showResults ? 'Viewing Report' : 'Wrap Onsite Payroll Report'}
          route="/edge-reporting"
        />
        {!!data?.length && showResults && (
          <CSVLink filename={fileName} data={getCSVData(data)}>
            <ImageColorChanger
              src={Excel}
              style={{ padding: '20px 30px 0 0 ', cursor: 'pointer' }}
              alt="export-to-excel"
            />
          </CSVLink>
        )}
      </div>
      {loadingResults && (
        <div className="overlay-box">
          <div className="spinner" />
        </div>
      )}
      {showResults ? (
        <>
          {!data.length && (
            <div className="wrap-onsite-payroll-container">
              <p>No data found for the specified criteria.</p>
            </div>
          )}
          {data.length > 0 && <WrapOnsitePayrollReportTable data={data} loading={loadingResults} />}
          <div className="buttons-row">
            <Button variant="primary" onPress={() => setShowResults(false)} style={{ marginRight: '20px', width: '100px' }} text="Back" />
            <Button
              variant="warning"
              style={{ width: '100px' }}
              onPress={() => navigate('/edge-reporting')}
              text="Cancel"
            />
          </div>
        </>
      ) : (
        <EdgeReportingWindow
          containerStyle={{
            border: '2px solid #343a40',
            paddingTop: '20px',
          }}
          windowContent={
            <WrapOnsitePayrollReportForm
              form={form}
              setForm={setForm}
              handleChange={handleChange}
              handleViewResults={handleViewResults}
              handleCancel={handleCancel}
            />
          }
          windowTitle="Criteria"
          headerStyle={{ backgroundColor: '#343a40' }}
        />
      )}
    </>
  );
};

export default WrapOnsitePayrollReport;
